export interface RollshopNotification {
	NotificationId: number;
	ActionTypeCode: "S" | "M" | "C" | "T"; // Swap, Mix, Create, Stand Type
	SourceCode: "M" | "R"; // Mill, Rollshop
	CreatedAt: Date;
	CreatedBy: string;
	StatusCode: "P" | "A" | "H"; // Pending, Acknowledged, Hold
	AcknowledgedBy: string | null;
	AcknowledgedAt: Date | null;
	Comments: string | null;
	Description: string | null;
}

export interface NotificationRollset {
	NotificationRollsetId: number;
	NotificationId: number;
	RollsetId: number;
	RoleCode: "T" | "B"; // Target, Broken

	// Rollset info
	RollsetLetter: string | null;
	SectionId: number | null;
	SectionName: string | null;
	StandId: number | null;
	StandName: string | null;

	// Current rolls (for Target rollsets)
	TopRollId: number | null;
	TopRollName: string | null;
	BottomRollId: number | null;
	BottomRollName: string | null;

	// Diameters
	TopRollDiameter: number | null;
	BottomRollDiameter: number | null;

	// Previous position info
	TopRollPrevPosition: string | null;
	BottomRollPrevPosition: string | null;
}

export interface DisruptionAlert {
	DisruptionAlertId: number;
	NotificationRollsetId: number;
	NotificationId: number;
	AlertTypeCode: "D" | "P" | "R"; // Diameter, Position, Rollset broken
	AlertMessage: string;
	CreatedAt: Date;

	// Type D: Diameter Mismatch
	DiameterTopRollId: number | null;
	DiameterTopRollName: string | null;
	TopDiameter: number | null;
	DiameterBottomRollId: number | null;
	DiameterBottomRollName: string | null;
	BottomDiameter: number | null;
	DiameterDifference: number | null;

	// Type P: Position Swap
	SwapRollId: number | null;
	SwapRollName: string | null;
	OriginalPosition: string | null;
	NewPosition: string | null;

	// Type R: Rollset Broken
	BrokenRollsetId: number | null;
	BrokenRollsetLetter: string | null;
	BrokenSectionName: string | null;
	BrokenStandName: string | null;
	OrphanedRollCount: number;
	OrphanedRollNames: string | null;
	OrphanedRollIds: string | null;
}

export interface StandTypeMismatchAlert {
	NotificationId: number;
	CarrierInstanceId: number;
	CarrierName: string;
	SectionId: number | null;
	SectionName: string | null;
	StandId: number | null;
	StandName: string | null;
	StandTypeCode: "U" | "E";
	RollsetId: number;
	RollsetLetter: string | null;
	TopRollId: number | null;
	TopRollName: string | null;
	TopRollTypeCode: "U" | "E" | null;
	BottomRollId: number | null;
	BottomRollName: string | null;
	BottomRollTypeCode: "U" | "E" | null;
}

export interface RollshopNotificationData {
	notifications: RollshopNotification[];
	rollsets: NotificationRollset[];
	alerts: DisruptionAlert[];
	standTypeMismatches: StandTypeMismatchAlert[];
}

// Helper to group data by NotificationId
export interface GroupedNotification extends RollshopNotification {
	targetRollsets: NotificationRollset[];
	brokenRollsets: NotificationRollset[];
	alerts: DisruptionAlert[];
	standTypeMismatch: StandTypeMismatchAlert | null;
}

export const groupNotificationData = (
	data: RollshopNotificationData,
): GroupedNotification[] => {
	return data.notifications.map((notification) => ({
		...notification,
		targetRollsets: data.rollsets.filter(
			(r) =>
				r.NotificationId === notification.NotificationId && r.RoleCode === "T",
		),
		brokenRollsets: data.rollsets.filter(
			(r) =>
				r.NotificationId === notification.NotificationId && r.RoleCode === "B",
		),
		alerts: data.alerts.filter(
			(a) => a.NotificationId === notification.NotificationId,
		),
		standTypeMismatch:
			data.standTypeMismatches.find(
				(m) => m.NotificationId === notification.NotificationId,
			) ?? null,
	}));
};
